import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { formatRelativeTime, formatDateShortUS } from "../utils/dateUtils";
import { normalizeImageUrl } from "../utils/urlUtils";
import { resolveProviderName } from "../utils/providerUtils";
import {
  navigateToGLDGame,
  navigateToCBGame,
} from "../../services/gameNavigationService";
import { usePlayMode } from "../../contexts/PlayModeContext";
import { trackEvent } from "../services/playerEvents";

// Memoized: the lobby renders many cards across several rows, and a single
// favorite toggle previously re-rendered every card (and re-ran each image's
// load). Props are primitives + stable callbacks, so referential memoization is
// safe and cheap. API is unchanged.
const GameCard = ({
  game,
  showJackpot = false,
  showLastPlayed = false,
  showAddedDate = false,
  showNameBelow = false,
  isFavorited = false,
  onToggleFavorite = null,
}) => {
  const navigate = useNavigate();
  const { isFunMode } = usePlayMode();

  const cardRef = useRef(null);

  // Transform and normalize game data internally
  const normalizedGame = {
    id: game.id || game.game_code,
    game_code: game.game_code, // Required for URL generation
    variant_group_id: game.variant_group_id, // Required for variant detection
    name: game.name,
    provider: resolveProviderName(game),
    // Prefer local_image_url (stored in Supabase) over external Hub88 url_thumb
    thumbnail: normalizeImageUrl(
      game.local_image_url ||
        game.thumbnail ||
        game.thumbnail_url ||
        game.url_thumb ||
        "/assets/games/default-game.png",
    ),
    category: game.category,
    rtp: game.rtp
      ? game.rtp.toString().includes("%")
        ? game.rtp
        : `${game.rtp}%`
      : null,
    volatility: game.volatility, // Only show if exists, no fallback
    minBet: game.minBet || game.min_bet || 1,
    maxBet: game.maxBet || game.max_bet || 100,
    jackpot: game.jackpot,

    isFavorite: game.isFavorite,
    lastPlayed: game.lastPlayed,
    createdAt:
      game.created_at ||
      game.createdAt ||
      game.release_date ||
      game.releaseDate,
  };

  // Launch game using default play mode
  const handleClick = () => {
    // player_events funnel: the lobby -> game step. This is the ONLY tile-click
    // path in the player app (lobby rows, category pages, provider pages and
    // search all render this card), so it is the whole of game_tile_click.
    // Without it the funnel jumps straight from lobby_viewed to
    // game_launch_attempt and cannot distinguish "never picked a game" from
    // "picked one and the launch failed".
    trackEvent("game_tile_click", {
      game_code: normalizedGame.game_code,
      currency: isFunMode ? "GLD" : "CB",
    });
    if (isFunMode) {
      navigateToGLDGame(navigate, normalizedGame);
    } else {
      navigateToCBGame(navigate, normalizedGame);
    }
  };

  const formatCurrency = (amount) => {
    // Format as CB (Coin Balance) for sweepstakes casino
    const formattedAmount = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
    return `${formattedAmount} CB`;
  };

  const formatLastPlayed = (timestamp) => {
    return formatRelativeTime(timestamp);
  };

  return (
    <div
      ref={cardRef}
      className="bg-card rounded-lg overflow-hidden shadow-lg hover:shadow-xl cursor-pointer group transition-all duration-300 motion-safe:hover:scale-105"
      onClick={handleClick}
    >
      <div className="relative">
        {/* Image container */}
        <div className="relative overflow-hidden rounded-lg aspect-[4/3]">
          <img
            src={normalizedGame.thumbnail}
            alt={normalizedGame.name}
            loading="lazy"
            decoding="async"
            width="400"
            height="300"
            className="w-full h-full object-cover transition-all duration-300 motion-safe:md:group-hover:blur-sm"
            ref={(node) => {
              // The global `img[loading="lazy"]` rule starts lazy images at
              // opacity:0 and only reveals them once a `.loaded` class is added.
              // A cached image can finish loading before React attaches onLoad,
              // so mark it loaded here too; otherwise it stays invisible.
              if (node && node.complete && node.naturalWidth > 0) {
                node.classList.add("loaded");
              }
            }}
            onLoad={(e) => e.currentTarget.classList.add("loaded")}
            onError={(e) => {
              // Hide broken images and show placeholder
              e.target.style.display = "none";
              const placeholder =
                e.target.parentElement.querySelector(".image-placeholder");
              if (placeholder) {
                placeholder.style.display = "flex";
              }
            }}
          />

          {/* Placeholder for broken images */}
          <div
            className="image-placeholder absolute inset-0 bg-secondary hidden items-center justify-center text-muted-foreground text-sm"
          >
            <div className="text-center">
              <div className="text-2xl mb-2">🎮</div>
              <div>Image Not Available</div>
            </div>
          </div>

          {/* Base bottom gradient and name overlay (under hover overlay) */}
          {showNameBelow && (
            <>
              <div className="absolute inset-x-0 bottom-0 h-16 md:h-20 bg-gradient-to-t from-black/70 via-black/40 to-transparent pointer-events-none" />
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-center pb-2 px-3 pointer-events-none">
                <span className="text-white text-sm md:text-base font-semibold text-center truncate drop-shadow-md">
                  {normalizedGame.name}
                </span>
              </div>
            </>
          )}

          {/* Hover Overlay - Desktop only */}
          <div className="absolute inset-0 bg-black/85 transition-opacity duration-300 flex flex-col items-center justify-center p-3 md:p-4 opacity-0 pointer-events-none md:group-hover:opacity-100 md:group-hover:pointer-events-auto">
            {/* Game Info */}
            <div className="text-center">
              <h3 className="font-bold text-white mb-2 text-base leading-tight">
                {normalizedGame.name}
              </h3>
              {normalizedGame.provider && (
                <p className="text-foreground text-sm mb-3">
                  {normalizedGame.provider}
                </p>
              )}

              {/* Game Stats - Only show bet range */}
              <div className="space-y-2">
                {game.minBet && game.maxBet && (
                  <div className="text-xs text-foreground tabular-nums">
                    Bet: {formatCurrency(game.minBet)} -{" "}
                    {formatCurrency(game.maxBet)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Jackpot Badge */}
        {showJackpot && normalizedGame.jackpot && (
          <div className="absolute top-2 left-2 bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-bold tabular-nums z-10">
            {typeof normalizedGame.jackpot === "string"
              ? normalizedGame.jackpot
              : formatCurrency(normalizedGame.jackpot)}
          </div>
        )}

        {/* Favorite Button - visible on desktop hover */}
        <button
          className="hidden md:flex absolute top-2 right-2 w-8 h-8 bg-card/80 hover:bg-secondary/80 rounded-lg items-center justify-center transition-all duration-200 z-20 cursor-pointer opacity-0 md:group-hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            if (onToggleFavorite) {
              onToggleFavorite(normalizedGame.id);
            }
          }}
        >
          <svg
            className={`w-4 h-4 transition-colors duration-200 ${
              isFavorited ? "text-destructive" : "text-muted-foreground"
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {/* Last Played Badge */}
        {showLastPlayed && normalizedGame.lastPlayed && (
          <div className="absolute top-2 left-2 bg-card/80 text-foreground px-2 py-1 rounded text-xs tabular-nums z-10 pointer-events-none transition-opacity duration-200 opacity-100 md:group-hover:opacity-0">
            {formatLastPlayed(normalizedGame.lastPlayed)}
          </div>
        )}

        {/* Added Date Badge for New Games */}
        {showAddedDate && normalizedGame.createdAt && (
          <div className="absolute top-2 left-2 bg-card/80 text-foreground px-2 py-1 rounded text-xs tabular-nums z-10 pointer-events-none transition-opacity duration-200 opacity-100 md:group-hover:opacity-0">
            {formatDateShortUS(normalizedGame.createdAt)}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(GameCard);
